import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectVersionsByProjectId } from "fond/api";
import { Maybe, MultiProject, Project, Store, Version } from "fond/types";
import { isDesignProject, isNBNProject, isProject, useIsCustomWorkflowComplete, useIsDesignProjectComplete } from "fond/utils/project";

import { useGetScenarioConfiguration } from "./useGetScenarioConfiguration";

interface UseVersionHasScheduleOfRatesReturn {
  /**
   * This version can have a schedule of rates.
   */
  possible: boolean;

  /**
   * This version has a schedule of rates available.
   */
  available: boolean;
}

interface UseVersionHasScheduleOfRatesProps {
  versionId?: string;
  project: Maybe<Project | MultiProject>;
}

const useVersionHasScheduleOfRates = ({ project, versionId }: UseVersionHasScheduleOfRatesProps): UseVersionHasScheduleOfRatesReturn => {
  const { hasConfig: isCollaborationCostToServe } = useGetScenarioConfiguration({ projectId: project?.ID });
  const versions = useSelector((state: Store): Version[] => (isProject(project) && selectVersionsByProjectId(state, project.ID)) || []);
  const rootVersionId = versions.at(-1)?.ID;

  // Only relevant for design-type projects
  const designVersionId = isDesignProject(project) ? versionId : undefined;
  const isDesignProjectComplete = useIsDesignProjectComplete({ versionId: designVersionId, project, rootVersionId });

  // Only relevant for collaboration projects having a scenario configuration
  const collaborationVersionId = isCollaborationCostToServe ? versionId : undefined;
  const hasGeneratedCollaborationCostToServe = useIsCustomWorkflowComplete({ versionId: collaborationVersionId, key: "collaboration_cost_to_serve" });

  const possible = isNBNProject(project) || isCollaborationCostToServe;
  const available =
    versionId && rootVersionId
      ? (isNBNProject(project) && isDesignProjectComplete) ||
        // collaboration cost-to-serve scenarios always have a schedule of rates;
        // the root version will have a schedule of rates if the collaboration_cost_to_serve workflow has been completed.
        (isCollaborationCostToServe && (versionId !== rootVersionId || hasGeneratedCollaborationCostToServe))
      : false;

  return useMemo(() => ({ possible, available }), [possible, available]);
};

export default useVersionHasScheduleOfRates;
